/**
 * VaultFilePreview: Full-screen lightbox for previewing files
 * Supports images, PDFs, videos, and audio files inline.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  X,
  Download,
  Maximize2,
  Minimize2,
  Loader2,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
} from 'lucide-react';
import type { VaultFile } from '../../types/vault.types';
import { getFileCategory, formatFileSize } from '../../types/vault.types';

interface VaultFilePreviewProps {
  file: VaultFile;
  isOpen: boolean;
  onClose: () => void;
  onDownload: (storagePath: string) => void;
  getDownloadUrl: (storagePath: string) => Promise<string>;
}

/** Which file categories can be previewed directly */
const PREVIEWABLE: Set<string> = new Set(['image', 'pdf', 'video', 'audio']);

// eslint-disable-next-line react-refresh/only-export-components -- utility tightly coupled with this component
export function isPreviewable(file: VaultFile): boolean {
  const cat = getFileCategory(file.mime_type, file.name);
  return PREVIEWABLE.has(cat);
}

export function VaultFilePreview({
  file,
  isOpen,
  onClose,
  onDownload,
  getDownloadUrl,
}: VaultFilePreviewProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const category = getFileCategory(file.mime_type, file.name);

  // Load signed URL on open
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      setUrl(null);
      try {
        const signedUrl = await getDownloadUrl(file.storage_path);
        if (!cancelled) {
          setUrl(signedUrl);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load preview');
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, file.storage_path, getDownloadUrl]);

  // ESC to close
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll while preview is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const getCategoryIcon = () => {
    switch (category) {
      case 'image': return <ImageIcon size={40} />;
      case 'pdf': return <FileText size={40} />;
      case 'video': return <Video size={40} />;
      case 'audio': return <Music size={40} />;
      default: return <FileText size={40} />;
    }
  };

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div className="vault-preview-loading">
          <Loader2 size={36} className="spin" />
          <p>Loading preview...</p>
        </div>
      );
    }

    if (error || !url) {
      return (
        <div className="vault-preview-error">
          {getCategoryIcon()}
          <p>{error || 'Unable to preview this file'}</p>
          <button
            className="vault-modal-btn primary"
            onClick={() => onDownload(file.storage_path)}
          >
            <Download size={14} /> Download Instead
          </button>
        </div>
      );
    }

    switch (category) {
      case 'image':
        return (
          <div className="vault-preview-image-wrap">
            <img
              src={url}
              alt={file.name}
              className="vault-preview-image"
              onError={() => setError('Failed to load image')}
            />
          </div>
        );

      case 'pdf':
        return (
          <iframe
            src={`${url}#toolbar=1&navpanes=0`}
            className="vault-preview-iframe"
            title={file.name}
          />
        );

      case 'video':
        return (
          <div className="vault-preview-video-wrap">
            <video
              src={url}
              controls
              autoPlay={false}
              className="vault-preview-video"
              onError={() => setError('Failed to load video')}
            >
              Your browser does not support this video format.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div className="vault-preview-audio-wrap">
            <div className="vault-preview-audio-icon">{getCategoryIcon()}</div>
            <span className="vault-preview-audio-name">{file.name}</span>
            <audio
              src={url}
              controls
              className="vault-preview-audio"
              onError={() => setError('Failed to load audio')}
            >
              Your browser does not support this audio format.
            </audio>
          </div>
        );

      default:
        return (
          <div className="vault-preview-error">
            {getCategoryIcon()}
            <p>Preview not available for this file type</p>
            <button
              className="vault-modal-btn primary"
              onClick={() => onDownload(file.storage_path)}
            >
              <Download size={14} /> Download
            </button>
          </div>
        );
    }
  };

  return (
    <div className="vault-preview-overlay" onClick={onClose}>
      <div
        className={`vault-preview-container ${fullscreen ? 'fullscreen' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="vault-preview-header">
          <div className="vault-preview-title">
            {getCategoryIcon()}
            <div className="vault-preview-title-info">
              <span className="vault-preview-filename">{file.name}</span>
              <span className="vault-preview-meta">
                {formatFileSize(file.size_bytes)} · {file.mime_type || 'Unknown type'}
              </span>
            </div>
          </div>

          <div className="vault-preview-actions">
            <button
              className="vault-preview-action-btn"
              onClick={() => onDownload(file.storage_path)}
              title="Download"
            >
              <Download size={18} />
            </button>
            <button
              className="vault-preview-action-btn"
              onClick={() => setFullscreen(!fullscreen)}
              title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {fullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button
              className="vault-preview-action-btn close"
              onClick={onClose}
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="vault-preview-body">
          {renderPreviewContent()}
        </div>
      </div>
    </div>
  );
}
